import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CSVData, VdpTemplate, VdpLayout, Unit } from '../types';
import { getAvailableSubstratesAsync } from '../utils/substratePresets';
import { BLANK_CANVAS_TEMPLATE } from '../utils/presetTemplates';

export interface UserProfileInfo {
  name: string;
  email: string;
  avatar: string;
  uid: string;
  role?: 'admin' | 'user' | 'operator';
  createdAt?: string;
  passwordHash?: string;
}

interface SaaSState {
  csvData: CSVData | null;
  setCsvData: (data: CSVData | null) => void;
  template: VdpTemplate;
  setTemplate: (t: VdpTemplate) => void;
  layout: VdpLayout;
  setLayout: React.Dispatch<React.SetStateAction<VdpLayout>>;
  appUnit: Unit;
  setAppUnit: (u: Unit) => void;
  showSettings: boolean;
  setShowSettings: (val: boolean) => void;
  showAuthDashboard: boolean;
  setShowAuthDashboard: (val: boolean) => void;
  showLocalAuthModal: boolean;
  setShowLocalAuthModal: (val: boolean) => void;
  startFreshProject: () => void;
  isAdmin: boolean;
  user: UserProfileInfo | null;
  setUser: (u: UserProfileInfo | null) => void;
  // Design Zoom & Transformation Persistence State
  zoomLevel: number;
  setZoomLevel: React.Dispatch<React.SetStateAction<number>>;
  panOffset: { x: number; y: number };
  setPanOffset: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  hasAutoFitted: boolean;
  setHasAutoFitted: React.Dispatch<React.SetStateAction<boolean>>;
  isFitToPage: boolean;
  setIsFitToPage: React.Dispatch<React.SetStateAction<boolean>>;
  isInitializing: boolean;
  setIsInitializing: React.Dispatch<React.SetStateAction<boolean>>;
  activeStep: 'data' | 'design' | 'imposition' | 'production';
  setActiveStep: (step: 'data' | 'design' | 'imposition' | 'production') => void;
  theme: 'dark' | 'light' | 'glassmorphism';
  setTheme: (t: 'dark' | 'light' | 'glassmorphism') => void;
  toggleTheme: () => void;
}

const defaultTemplate: VdpTemplate = BLANK_CANVAS_TEMPLATE;

const defaultLayout: VdpLayout = {
  pageSize: 'A4',
  pageOrientation: 'portrait',
  rows: 4,
  cols: 2,
  gapX: 10,  
  gapY: 10,
  marginX: 30,
  marginY: 30,
  showTrimMarks: false,
  trimMarkType: 'outer_grid',
  trimMarkOffset: 8.5,
  trimMarkLength: 10,
  exportMode: 'multi_up'
};

const LOCAL_STORAGE_KEY_TEMPLATE = 'vdp_current_template_v2';
const LOCAL_STORAGE_KEY_LAYOUT = 'vdp_current_layout_v2';

const SaaSContext = createContext<SaaSState | undefined>(undefined);

export const SaaSProvider = ({ children }: { children?: ReactNode }) => {
  const [csvData, setCsvData] = useState<CSVData | null>(null);
  
  const [template, setTemplate] = useState<VdpTemplate>(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY_TEMPLATE);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error("Failed to parse saved template", e);
    }
    return defaultTemplate;
  });
  
  const [layout, setLayout] = useState<VdpLayout>(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY_LAYOUT);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error("Failed to parse saved layout", e);
    }
    return defaultLayout;
  });
  
  const [appUnit, setAppUnit] = useState<Unit>('pt');
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAuthDashboard, setShowAuthDashboard] = useState<boolean>(false);
  const [showLocalAuthModal, setShowLocalAuthModal] = useState<boolean>(false);

  // User Session State (Stored per local user account)
  const [user, setUser] = useState<UserProfileInfo | null>(() => {
    try {
      const saved = localStorage.getItem('vdp_current_user_session');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure valid user object exists in session
        if (!parsed?.email || !parsed?.uid) {
          localStorage.removeItem('vdp_current_user_session');
          return null;
        }
        return parsed;
      }
    } catch (e) {
      console.error("Failed to parse saved local user session", e);
    }
    return null;
  });

  // Load user specific template & layout on user session change
  useEffect(() => {
    if (user?.uid) {
      try {
        const userTplKey = `vdp_user_${user.uid}_template_v2`;
        const userLayKey = `vdp_user_${user.uid}_layout_v2`;
        const savedTpl = localStorage.getItem(userTplKey);
        if (savedTpl) setTemplate(JSON.parse(savedTpl));
        const savedLay = localStorage.getItem(userLayKey);
        if (savedLay) setLayout(JSON.parse(savedLay));
      } catch (e) {
        console.error("Failed to load user specific data", e);
      }
    }
  }, [user?.uid]);

  // Persistence for Design Canvas transformation & zoom
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hasAutoFitted, setHasAutoFitted] = useState<boolean>(false);
  const [isFitToPage, setIsFitToPage] = useState<boolean>(true);
  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<'data' | 'design' | 'imposition' | 'production'>('data');

  // Theme support (Dark Mode, Light Mode, and Aurora Glassmorphism)
  const [theme, setThemeState] = useState<'dark' | 'light' | 'glassmorphism'>(() => {
    try {
      const saved = localStorage.getItem('vdp_app_theme');
      if (saved === 'light' || saved === 'dark' || saved === 'glassmorphism') return saved as any;
    } catch {}
    return 'glassmorphism';
  });

  const setTheme = (newTheme: 'dark' | 'light' | 'glassmorphism') => {
    setThemeState(newTheme);
    try {
      localStorage.setItem('vdp_app_theme', newTheme);
    } catch {}
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : theme === 'light' ? 'glassmorphism' : 'dark';
    setTheme(next);
  };

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.remove('light', 'dark', 'theme-glassmorphism');
    body.classList.remove('light', 'dark', 'theme-glassmorphism');
    if (theme === 'light') {
      root.classList.add('light');
      body.classList.add('light');
      root.setAttribute('data-theme', 'light');
    } else if (theme === 'glassmorphism') {
      root.classList.add('dark', 'theme-glassmorphism');
      body.classList.add('dark', 'theme-glassmorphism');
      root.setAttribute('data-theme', 'glassmorphism');
    } else {
      root.classList.add('dark');
      body.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
    }
  }, [theme]);

  useEffect(() => {
    // Fallback safety timeout in case splash screen is not dismissed
    const fallbackTimer = setTimeout(() => setIsInitializing(false), 5000);
    return () => clearTimeout(fallbackTimer);
  }, []);

  // Auto-save template & layout per local user ID
  useEffect(() => {
    try {
      const key = user?.uid ? `vdp_user_${user.uid}_template_v2` : LOCAL_STORAGE_KEY_TEMPLATE;
      localStorage.setItem(key, JSON.stringify(template));
    } catch (e) {
      console.warn("Failed to write template to localStorage", e);
    }
  }, [template, user?.uid]);

  useEffect(() => {
    try {
      const key = user?.uid ? `vdp_user_${user.uid}_layout_v2` : LOCAL_STORAGE_KEY_LAYOUT;
      localStorage.setItem(key, JSON.stringify(layout));
    } catch (e) {
      console.warn("Failed to write layout to localStorage", e);
    }
  }, [layout, user?.uid]);

  useEffect(() => {
    getAvailableSubstratesAsync().catch(() => {});
  }, []);

  const isAdmin = Boolean(user?.role === 'admin');

  const startFreshProject = () => {
    setTemplate({
      width: 250,
      height: 150,
      backgroundColor: '#ffffff',
      elements: []
    });
    setCsvData(null);
    setLayout(defaultLayout);
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
    setHasAutoFitted(false);
    localStorage.removeItem(LOCAL_STORAGE_KEY_TEMPLATE);
    localStorage.removeItem(LOCAL_STORAGE_KEY_LAYOUT);
  };

  const value = React.useMemo(() => ({ 
    csvData, setCsvData, 
    template, setTemplate, 
    layout, setLayout, 
    appUnit, setAppUnit,
    showSettings, setShowSettings,
    showAuthDashboard, setShowAuthDashboard,
    showLocalAuthModal, setShowLocalAuthModal,
    startFreshProject,
    isAdmin,
    user, setUser,
    zoomLevel, setZoomLevel,
    panOffset, setPanOffset,
    hasAutoFitted, setHasAutoFitted,
    isFitToPage, setIsFitToPage,
    isInitializing, setIsInitializing,
    activeStep, setActiveStep,
    theme, setTheme, toggleTheme
  }), [
    csvData, template, layout, appUnit, showSettings, showAuthDashboard, showLocalAuthModal,
    isAdmin, user,
    zoomLevel, panOffset, hasAutoFitted, isFitToPage, isInitializing, activeStep,
    theme
  ]);

  return (
    <SaaSContext.Provider value={value}>
      {children}
    </SaaSContext.Provider>
  );
};

export const useSaaSContext = () => {
  const ctx = useContext(SaaSContext);
  if (!ctx) throw new Error('useSaaSContext must be used within SaaSProvider');
  return ctx;
};
